import React, { useCallback, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';

import agGridConfig from '../../agGridConfig';
import Config from '../../config';
import { adminPageTitles } from '../shared/adminPageNames';

import {
  categoriesStaticSelector,
  environmentSuffixSelector,
  periodOrCurrentSelector,
} from '../../shared/store/selectors';

import useSelectAgGridRow from '../../shared/hooks/useSelectAgGridRow.hook';
import { displayZeroSelector, hideDecimalsSelector, negativeMinusSelector } from '../../userData/store/selectors';

import {
  idOfLastJobOfTypeSelectorFactory,
  isLastJobOfTypeInProgressSelectorFactory,
  isLastJobOfTypeSucceededSelectorFactory, lastJobOfTypeSelectorFactory,
} from '../../shared/jim/store/selectors';
import { Job } from '../../menu/jobsMenu/types';
import { Period } from '../../shared/types';

import AdminImportTabsWithJimImport from '../shared/adminImportPageWithJimImport.component';
import Spinner from '../../shared/spinner.component';
import { AuthorizeUserTree, permissions, permissionTypes, withPermissions } from '../../shared/authorization';

import useRemoveModalControls from '../clientAdministrationPage/shared/useRemoveModalControls';
import { TWithValidationError } from '../clientAdministrationPage/shared/useUploadEntities';

import AddEditTlsAccountModal from './addTLSAccountModal.component';
import DeleteTlsAccountModal from './deleteTLSAccountModal.component';
import downloadTlsAccountsXlsx from './downloadTlsAccountsXlsx';
import useAddEditModalControls from '../clientAdministrationPage/shared/useAddEditModalControls';
import { SaveEntityFunction } from '../clientAdministrationPage/types';
import { useAddTlsAccount, useDeleteTlsAccount, useUpdateTlsAccount } from '@mutations/tlsAccounts';
import { useTlsAccounts } from '@queries/tlsAccounts';
import { TlsAccount } from '@common/schemas/tlsAccount';
import { TlsAccountsAdminColumnRendererFactory } from './tlsAccountSettingsAdminColumnRenderer.component';
import { waitForSpinnerToShowUp } from '../../shared/cursorHelpers';

import { generatingR2RChartSelector } from './store/selectors';
import { generateR2RChart } from './store/actions';
import getColumnDefinitions from '../../shared/columnDefinitions/tlsAccounts';


const isTlsAccountsValidationInProgressSelector = isLastJobOfTypeInProgressSelectorFactory(
  Config.jimJobs.tlsAccountsValidate.type
);

const hasLastTlsAccountsValidateJobSucceedSelector = isLastJobOfTypeSucceededSelectorFactory(
  Config.jimJobs.tlsAccountsValidate.type
);

const isTlsAccountsImportInProgressSelector = isLastJobOfTypeInProgressSelectorFactory(
  Config.jimJobs.tlsAccountsImport.type
);

const idOfLastTlsAccountsValidateJobSelector = idOfLastJobOfTypeSelectorFactory(
  Config.jimJobs.tlsAccountsValidate.type
);

const lastTlsAccountsImportingJobSelector = lastJobOfTypeSelectorFactory(Config.jimJobs.tlsAccountsImport.type);

const useRenderGenerateR2RChartComponent = () => {
  const [isGeneratingR2RChart, setIsGeneratingR2RChart] = useState(false);

  const generatingR2RChart = useSelector(generatingR2RChartSelector);
  const dispatch = useDispatch();

  const generateR2RChartAction = useCallback(callback => {
    if (!generatingR2RChart) {
      dispatch(generateR2RChart());
    }

    if (callback) {
      callback();
    }
  }, [dispatch, generatingR2RChart]);

  const generateR2RChartHandler = useCallback(() => {
    setIsGeneratingR2RChart(true);
    waitForSpinnerToShowUp({
      action: generateR2RChartAction,
      callback: () => setIsGeneratingR2RChart(false),
    });
  }, [generateR2RChartAction]);

  const renderGenerateR2RChartComponent = useCallback(() => (
    <AuthorizeUserTree
      withPermissions={[{
        permissionType: permissionTypes.MODIFY,
        permissionPath: permissions.TLS_ACCOUNT_SETTINGS,
      }]}
    >
      <button
        className="hamburger-action link-button"
        tabIndex={0}
        onClick={generateR2RChartHandler}
        disabled={false}
        key="generateR2RChartOption"
      >
        {isGeneratingR2RChart
          ? (
            <span>
              <Spinner small /> Generating
            </span>
          ) : 'Generate R2R Chart'
        }
      </button>
    </AuthorizeUserTree>
  ), [generateR2RChartHandler, isGeneratingR2RChart]);

  return renderGenerateR2RChartComponent;
};

const columnDefinitions = getColumnDefinitions(categoriesStaticSelector());

type TLSAccountsPageProps = {
  period: Period,
  canModifyInSelectedPeriod: boolean,
  hideDecimals: boolean,
  shouldDisplayZero: boolean,
  environmentSuffix: string,
  negativeMinus: boolean,
  idOfLastTlsAccountsValidateJob: Job['jobId'],
  isTlsAccountsValidationInProgress: boolean,
  hasLastTlsAccountsValidateJobSucceed: boolean,
  isTlsAccountsImportInProgress: boolean,
  tlsAccountsImportingJob: Job,
  categories: {
    categoryName: string,
    description: string,
  }[],
}
const TLSAccountsPage = ({
  canModifyInSelectedPeriod,
  period,
  hideDecimals,
  shouldDisplayZero,
  environmentSuffix,
  negativeMinus,
  isTlsAccountsImportInProgress,
  categories,
}: TLSAccountsPageProps) => {
  const {
    selectAgGridRow,
    selectAgGridRowByIndex,
  } = useSelectAgGridRow(agGridConfig.adminTLSAccounts);

  const {
    hideModalForAddEdit,
    showModalForAdd,
    showModalForEdit,
    entityToEdit,
    isAddEditModalVisible,
  } = useAddEditModalControls<TlsAccount>();

  const {
    hideModalForRemove,
    showModalForRemove,
    entityToRemove,
    rowToRemoveIndex,
    isRemoveModalVisible,
  } = useRemoveModalControls<TlsAccount>();

  const {
    isFetching: isFetchingEntities,
    data: entities,
    refetch: fetchEntities,
  } = useTlsAccounts({ periodName: period.name });

  const { mutate: addEntity, isLoading: isAddingEntity } = useAddTlsAccount();
  const { mutate: updateEntity, isLoading: isUpdatingEntity } = useUpdateTlsAccount();
  const { mutate: removeEntity, isLoading: isRemovingEntity } = useDeleteTlsAccount(() => {
    hideModalForRemove();
    selectAgGridRowByIndex(rowToRemoveIndex ? rowToRemoveIndex - 1 : 0);
  });

  const onSaveSuccess = useCallback((formData, { keepOpenAfterSaving, callback }) => {
    if (!keepOpenAfterSaving) {
      hideModalForAddEdit();
      selectAgGridRow(formData);
    }

    if (callback) {
      return callback();
    }
  }, [hideModalForAddEdit, selectAgGridRow]);

  const saveEntity: SaveEntityFunction<TlsAccount> = useCallback(
    async (formData, openForEdit, options) => {
      if (openForEdit) {
        updateEntity(
          { ...formData, periodName: period.name },
          { onSuccess: () => onSaveSuccess(formData, options) }
        );
      } else {
        addEntity(
          { ...formData, periodName: period.name },
          { onSuccess: () => onSaveSuccess(formData, options) }
        );
      }
    }, [addEntity, updateEntity, onSaveSuccess, period]
  );

  const isSavingEntity = isAddingEntity || isUpdatingEntity;

  const generateTemplateFileName = useCallback(
    ({ periodName }) => `TLS_Accounts_${periodName}`,
    []
  );


  const adminColumnRenderer = useMemo(() => TlsAccountsAdminColumnRendererFactory({
    showModalForEdit,
    showModalForRemove,
  }), [
    showModalForEdit,
    showModalForRemove,
  ]);


  const renderGenerateR2RChartComponent = useRenderGenerateR2RChartComponent();

  return (
    <AdminImportTabsWithJimImport<
      TlsAccount,
      TlsAccount,
      TWithValidationError<TlsAccount>
    >
      pageTitle={adminPageTitles.TLS_ACCOUNTS}

      canModify={canModifyInSelectedPeriod}

      AddEditEntityModal={AddEditTlsAccountModal}
      RemoveEntityModal={DeleteTlsAccountModal}
      adminColumnRenderer={adminColumnRenderer}
      addEntityMessage="Add TLS Account"
      columnDefs={columnDefinitions}
      exportToExcel={downloadTlsAccountsXlsx(categories)}
      generateTemplateFileName={generateTemplateFileName}
      templatePath={Config.templates.TLS_ACCOUNTS}
      comparatorConfig={{
        idField: 'account',
      }}

      isFetchingEntities={isFetchingEntities}
      fetchEntities={fetchEntities}
      entities={entities}

      saveEntity={saveEntity}
      isSavingEntity={isSavingEntity}

      removeEntity={removeEntity}
      isRemovingEntity={isRemovingEntity}

      period={period}

      isImportingEntities={isTlsAccountsImportInProgress}

      hideDecimals={hideDecimals}
      shouldDisplayZero={shouldDisplayZero}
      environmentSuffix={environmentSuffix}
      negativeMinus={negativeMinus}


      isAddEditModalVisible={isAddEditModalVisible}
      entityToEdit={entityToEdit}

      isRemoveModalVisible={isRemoveModalVisible}
      entityToRemove={entityToRemove}
      rowToRemoveIndex={rowToRemoveIndex}

      showModalForAdd={showModalForAdd}
      hideModalForAddEdit={hideModalForAddEdit}
      hideModalForRemove={hideModalForRemove}

      additionalHamburgerMenuItems={[renderGenerateR2RChartComponent()]}

      validateJobConfig={Config.jimJobs.tlsAccountsValidate}
      importJobConfig={Config.jimJobs.tlsAccountsImport}
    />
  );
};


const mapStateToProps = (state: { shared: any, user: any }) => ({
  period: periodOrCurrentSelector(state),
  tlsAccountsImportingJob: lastTlsAccountsImportingJobSelector(state),
  categories: categoriesStaticSelector(),

  environmentSuffix: environmentSuffixSelector(state),
  shouldDisplayZero: displayZeroSelector(state),
  hideDecimals: hideDecimalsSelector(state),
  negativeMinus: negativeMinusSelector(state),

  idOfLastTlsAccountsValidateJob: idOfLastTlsAccountsValidateJobSelector(state),
  isTlsAccountsValidationInProgress: isTlsAccountsValidationInProgressSelector(state),
  hasLastTlsAccountsValidateJobSucceed: hasLastTlsAccountsValidateJobSucceedSelector(state),
  isTlsAccountsImportInProgress: isTlsAccountsImportInProgressSelector(state),
});

export default withPermissions(permissions.TLS_ACCOUNT_SETTINGS)(
  connect(mapStateToProps)(TLSAccountsPage)
);
